// React Imports
import React, { useState, useEffect } from 'react';

// MUI Imports
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import InputLabel from '@mui/material/InputLabel';
import { grey, green } from '@mui/material/colors';
import FormControl from '@mui/material/FormControl';
import VaccinesIcon from "@mui/icons-material/Vaccines";
import FormHelperText from '@mui/material/FormHelperText';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import CoronavirusIcon from "@mui/icons-material/Coronavirus";
import FormControlLabel from '@mui/material/FormControlLabel';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import { Avatar, Checkbox, Chip, InputAdornment, Typography, Button, Stack, LinearProgress } from "@mui/material";

// Component Imports
import InfoImage from "../InfoImage";
import StudentPhoto from "../StudentPhoto/StudentPhoto";
import MedicareExpiryDatePicker from '../MedicareExpiryDatePicker/MedicareExpiryDatePicker';
import MedicareUpload from '../ParentInformation/MedicareUpload';
import IOSSegmentedControl from '../common/IOSSegmentedControl';
import {
    getStudentFullName,
    getStudentGivenName,
    isEligibleForReenrolment,
} from '../../utils/parentHistoryHelpers';
import {
    getStudentMedicalErrors,
    isValidMonthYear,
    isMonthYearInFuture,
    splitMedicareNumber,
    getMedicareBase,
    getMedicareRef,
} from '../../utils/studentMedicalValidation';
import classes from './StudentMedicalForm.module.css'

// Image imports
import medicareImage3 from './medicareImage3.png';
import medicareImage4 from './medicareImage4.png';
import medicareImage5 from './medicareImage5.png';
import medicareImage6 from './medicareImage6.png';
import medicareImage7 from './medicareImage7.png';
import medicarecard from './MedicareCard.jpg';
import { cardSideMissing } from '../../utils/storedCardImages';

const YES_VALUES = new Set(['YES', 'Y', '1', 'TRUE']);
const NO_VALUES = new Set(['NO', 'N', '0', 'FALSE']);

/** Trim / coerce TASS-style yes-no values to YES | NO | null for selects */
const normalizeYesNo = (value) => {
    if (value == null || value === '') return null;
    const normalized = String(value).trim().toUpperCase();
    if (!normalized) return null;
    if (YES_VALUES.has(normalized)) return 'YES';
    if (NO_VALUES.has(normalized)) return 'NO';
    return normalized;
};

const getStudentDisplayName = (item) => getStudentFullName(item);

const getStudentCode = (item) => item.student_code || item.stud_code || '';
const isNotEligibleForReenrolment = (item) => !isEligibleForReenrolment(item);

const StudentMedicalForm = (props) => {

    const [formData, setFormData] = useState([]);
    const [medicareBackup, setMedicareBackup] = useState({});
    const [activeStudentTab, setActiveStudentTab] = useState(0);


    const validateMedicareDetails = (value) => {
        const parsed = splitMedicareNumber(value);
        return /^\d{10}$/.test(parsed.base) && /^\d$/.test(parsed.ref);
    };

    /** Fields still outstanding across every student being re-enrolled. */
    const validateMedicalFields = (data = formData) => {
        const errors = (data || []).flatMap((item, index) =>
            getStudentMedicalErrors(item, {
                parentNoMedicare: props.parentNoMedicare,
                parentMedicareNumber: props.parentMedicareNumber,
                parentMedicareExpiry: props.parentMedicareExpiry,
                studentMedicareImages: props.studentMedicareImages?.[getStudentCode(item) || index] || [],
                studentStoredCard: props.studentStoredCards?.[getStudentCode(item)] || [],
            })
        );
        const hasError = (...fields) => errors.some((error) => fields.includes(error.field));

        props.SetValidMedicare?.(!hasError('medicare_number', 'medicare_ref_no', 'family_medicare_number'));
        props.SetValidMedicareExpiry?.(!hasError('medicare_expiry', 'family_medicare_expiry'));
        props.SetValidAmbulanceCover?.(!hasError('ambulance_cover'));
        props.SetValidPHI?.(!hasError('private_health_insurance'));
        props.SetValidImmunisationStatus?.(!hasError('immunisation_status'));
        props.SetValidAllergy?.(!hasError('allergy'));
    };

    useEffect(() => {
        const updatedStudents = props.items.map((student) => {
            const isNotEligible = isNotEligibleForReenrolment(student);
            const enrollStatus = isNotEligible ? 'Not Enrolled' : (student.enrollStatus || 'Enrolled');

            const medicareNumber = student.medicare_number == null
                ? null
                : String(student.medicare_number).trim() || null;
            const medicareExpiry = student.medicare_expiry == null
                ? null
                : String(student.medicare_expiry).trim() || null;

            const parsedMedicare = splitMedicareNumber(medicareNumber);
            const medicareRef = String(student.medicare_ref_no ?? '').trim()
                || parsedMedicare.ref;
            const isTempVisa = Boolean(student.istemporaryVisa ?? student.temporary_visa);
            const normalizedMedicareNumber = medicareNumber && medicareNumber.toUpperCase() !== 'NA' && parsedMedicare.base
                ? (medicareRef ? `${parsedMedicare.base}#${medicareRef}` : parsedMedicare.base)
                : medicareNumber;

            return {
                ...student,
                medicare_ref_no: /^\d$/.test(medicareRef) ? medicareRef : (student.medicare_ref_no ?? ''),
                student_code: getStudentCode(student),
                student_name: getStudentGivenName(student),
                student_surname: student.student_surname || student.surname || '',
                ambulance_cover: normalizeYesNo(student.ambulance_cover),
                private_health_insurance: (() => {
                    const normalized = normalizeYesNo(
                        student.private_health_insurance ?? student.private_healthinsurance
                    );
                    if (normalized === 'YES') return 'Y';
                    if (normalized === 'NO') return 'N';
                    return null;
                })(),
                immunisation_status: normalizeYesNo(student.immunisation_status),
                allergy: (() => {
                    const normalized = normalizeYesNo(student.allergy);
                    if (normalized === 'YES') return 'Y';
                    if (normalized === 'NO') return 'N';
                    return null;
                })(),
                medicare_number: normalizedMedicareNumber,
                medicare_expiry: medicareExpiry,
                validMedicareNumber: validateMedicareDetails(normalizedMedicareNumber),
                temporary_visa: isTempVisa,
                istemporaryVisa: isTempVisa,
                enrollStatus,
                notEligibleForReenrolment: isNotEligible,
            };
        });

        validateMedicalFields(updatedStudents);
        props.setUpdateMedicalRecords(updatedStudents);
        setFormData(updatedStudents);
    }, [props.items]);

    const updateFormAndProps = (data) => {
        props.setUpdateMedicalRecords(data);
        setFormData(data);

        // Recompute validation flags for parent on every change
        validateMedicalFields(data);
    };

    const clearStudentCardImages = (index) => {
        props.setStudentMedicareImages?.(getStudentCode(formData[index]) || index, []);
    };

    const handleInputChange = (index, key, value) => {
        const updatedData = [...formData];

        const updateEntry = (newFields) => {
            // If switching to temporary visa, set Medicare to NA
            if (key === "temporary_visa" && newFields.temporary_visa) {
                newFields.medicare_number = "NA";
                newFields.medicare_expiry = "NA";
                newFields.validMedicareNumber = null;
            }

            updatedData[index] = { ...updatedData[index], ...newFields };
            updateFormAndProps(updatedData);
        };

        switch (key) {
            case 'medicare_ref_no': {
                const parsed = splitMedicareNumber(formData[index].medicare_number);
                const combined = value && parsed.base ? `${parsed.base}#${value}` : parsed.base;
                updateEntry({
                    [key]: value,
                    medicare_number: combined || formData[index].medicare_number,
                    validMedicareNumber: validateMedicareDetails(combined),
                });
                break;
            }

            case 'hasCustomMedicare': {
                updateEntry({
                    [key]: value
                });
                if (!value) {
                    props.setStudentMedicareImages?.(getStudentCode(formData[index]) || index, []);
                }
                break;
            }

            case 'medicare_number': {
                const isValid = validateMedicareDetails(value);
                updateEntry({
                    [key]: value,
                    validMedicareNumber: value.length < 13 ? isValid : null
                });
                break;
            }

            case 'temporary_visa': {
                if (value) {
                    // Store backup only if not already saved
                    if (!medicareBackup[index]) {
                        setMedicareBackup(prev => ({
                            ...prev,
                            [index]: {
                                medicare_number: formData[index].medicare_number,
                                medicare_expiry: formData[index].medicare_expiry,
                            }
                        }));
                    }

                    const visaFields = {
                        [key]: value,
                        istemporaryVisa: value,
                        medicare_number: 'NA',
                        medicare_expiry: 'NA',
                        validMedicareNumber: null,
                    };

                    updateEntry(visaFields);
                } else {
                    const backup = medicareBackup[index] || {
                        medicare_number: '',
                        medicare_expiry: '',
                    };

                    const visaFields = {
                        [key]: value,
                        istemporaryVisa: value,
                        medicare_number: backup.medicare_number,
                        medicare_expiry: backup.medicare_expiry,
                        validMedicareNumber: validateMedicareDetails(backup.medicare_number),
                    };

                    updateEntry(visaFields);
                }

                break;
            }

            case 'medicare_expiry': {
                if (value?.$d) {
                    const formattedExpiry = `${value.$d.getMonth() + 1}/${value.$d.getFullYear()}`;
                    if (formattedExpiry !== formData[index]?.medicare_expiry) {
                        clearStudentCardImages(index);
                    }
                    updateEntry({ [key]: formattedExpiry });
                } else {
                    clearStudentCardImages(index);
                    updateEntry({ [key]: '' });
                }
                break;
            }

            case 'ambulance_cover':
            case 'private_health_insurance':
            case 'immunisation_status':
            case 'allergy':
                // updateEntry re-runs validateMedicalFields across every student,
                // so these must not be optimistically marked valid here.
                updateEntry({ [key]: value });
                break;

            default:
                updateEntry({ [key]: value });
        }
    };

    const handleMedicarePartChange = (index, part, value) => {
        const current = formData[index].medicare_number || '';
        const parsed = splitMedicareNumber(current);
        let main = parsed.base;
        let suffix = parsed.ref;

        if (part === 'main') main = value.replace(/\D/g, '').substring(0, 10);
        if (part === 'suffix') suffix = value.replace(/\D/g, '').substring(0, 1);

        const combined = suffix ? `${main}#${suffix}` : main;
        const isValid = validateMedicareDetails(combined);
        if (combined !== current) clearStudentCardImages(index);

        const updatedData = [...formData];
        updatedData[index] = {
            ...updatedData[index],
            medicare_number: combined,
            medicare_ref_no: suffix,
            validMedicareNumber: combined.length >= 12 ? isValid : null,
        };

        updateFormAndProps(updatedData);
    };


    const renderItems = formData.map((item, index) => {
        const isStudentNotEnrolled = item.enrollStatus === 'Not Enrolled';
        const isNotEligible = item.notEligibleForReenrolment === true;
        const displayName = getStudentDisplayName(item);
        const studentCode = getStudentCode(item);

        return (
            <React.Fragment key={index}>
                <div className={`${classes.databox}`}>
                    <div className={`${classes.datainnerbox}`}>
                        <table className={`${classes.FeeTblinr}`}>
                            <tbody className={`${classes.tbody}`}>
                                <>
                                    <tr>
                                        <td className={classes.Btnstd}>
                                            <Box className={classes.studentHeader}>
                                                <Box className={classes.studentImageSection}>
                                                    <StudentPhoto
                                                        src={item.imageUrl}
                                                        name={displayName}
                                                        imageClassName={classes.studentImage}
                                                        avatarClassName={classes.studentAvatar}
                                                    />
                                                </Box>
                                                <Box className={classes.studentInfoSection}>
                                                    <Box className={classes.studentNameContainer}>
                                                        <Box className={classes.studentNameRow}>
                                                            <Typography
                                                                variant="h6"
                                                                className={classes.studentName}
                                                                sx={{ marginBottom: '4px' }}
                                                            >
                                                                {displayName}
                                                            </Typography>
                                                            {item.campus_name && (
                                                                <Chip
                                                                    label={item.campus_name}
                                                                    size="small"
                                                                    className={classes.campusChip}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            className={classes.studentCode}
                                                            sx={{
                                                                color: '#666',
                                                                fontWeight: 500,
                                                                marginBottom: '8px'
                                                            }}
                                                        >
                                                            Student ID: {studentCode}
                                                        </Typography>
                                                    </Box>
                                                    <Box className={classes.enrollmentSection}>
                                                        <FormControl>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={item.enrollStatus === 'Enrolled'}
                                                                        disabled={isNotEligible}
                                                                        onChange={(e) =>
                                                                            handleInputChange(index, 'enrollStatus', e.target.checked ? 'Enrolled' : 'Not Enrolled')
                                                                        }
                                                                        sx={{
                                                                            '&.Mui-checked': {
                                                                                color: green[600],
                                                                            },
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    isNotEligible
                                                                        ? `Not Eligible for Re-enrolment`
                                                                        : (item.enrollStatus === 'Enrolled'
                                                                            ? `Re-enrolling ${displayName}`
                                                                            : `I want to Re-enroll ${displayName}`)
                                                                }
                                                            />
                                                        </FormControl>
                                                        <Chip
                                                            label={item.enrollStatus === 'Enrolled' ? 'Selected for Re-enrolment' : 'Not Re-enrolling'}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: item.enrollStatus === 'Enrolled' ? '#ECFDF5' : '#FEF2F2',
                                                                color: item.enrollStatus === 'Enrolled' ? '#065F46' : '#991B1B',
                                                                fontWeight: 700,
                                                                fontSize: '12px',
                                                                border: item.enrollStatus === 'Enrolled' ? '1px solid #A7F3D0' : '1px solid #FCA5A5'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={1}>
                                            <Box sx={{ 
                                                display: 'grid', 
                                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
                                                gap: '20px', 
                                                width: '100%', 
                                                paddingBottom: '10px' 
                                            }}>
                                                <Box id="student-field-ambulance_cover" sx={{ flex: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                                    <InputLabel className={classes.selectLabel} sx={{ mb: 0 }}>Ambulance Cover (Required)</InputLabel>
                                                    <InfoImage
                                                        image={medicareImage4}
                                                        title="Ambulance Cover"
                                                    />
                                                </Box>
                                                <IOSSegmentedControl
                                                    options={[{ label: 'YES', value: 'YES' }, { label: 'NO', value: 'NO' }]}
                                                    value={item.ambulance_cover}
                                                    onChange={(val) => handleInputChange(index, 'ambulance_cover', val)}
                                                    disabled={isStudentNotEnrolled}
                                                    error={(item.ambulance_cover === null || item.ambulance_cover === undefined) && !isStudentNotEnrolled}
                                                />
                                                {((item.ambulance_cover === null || item.ambulance_cover === undefined) && !isStudentNotEnrolled) && <FormHelperText className={classes.FormHelperText}>Ambulance Cover cannot be empty</FormHelperText>}
                                            </Box>
                                            <Box id="student-field-private_health_insurance" sx={{ flex: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                                    <InputLabel className={classes.selectLabel} sx={{ mb: 0 }}>Private Health Insurance (Required)</InputLabel>
                                                    <InfoImage
                                                        image={medicareImage5}
                                                        title="Private Health Insurance"
                                                    />
                                                </Box>
                                                <IOSSegmentedControl
                                                    options={[{ label: 'YES', value: 'Y' }, { label: 'NO', value: 'N' }]}
                                                    value={item.private_health_insurance}
                                                    onChange={(val) => handleInputChange(index, 'private_health_insurance', val)}
                                                    disabled={isStudentNotEnrolled}
                                                    error={(item.private_health_insurance === null || item.private_health_insurance === undefined) && !isStudentNotEnrolled}
                                                />
                                                {((item.private_health_insurance === null || item.private_health_insurance === undefined) && !isStudentNotEnrolled) && <FormHelperText className={classes.FormHelperText}>Private Health Insurance cannot be empty</FormHelperText>}
                                            </Box>
                                            <Box id="student-field-immunisation_status" sx={{ flex: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                                    <InputLabel className={classes.selectLabel} sx={{ mb: 0 }}>Immunisation Status (Required)</InputLabel>
                                                    <InfoImage
                                                        image={medicareImage6}
                                                        title="Immunisation Status"
                                                    />
                                                </Box>
                                                <IOSSegmentedControl
                                                    options={[{ label: 'YES', value: 'YES' }, { label: 'NO', value: 'NO' }]}
                                                    value={item.immunisation_status}
                                                    onChange={(val) => handleInputChange(index, 'immunisation_status', val)}
                                                    disabled={isStudentNotEnrolled}
                                                    error={(item.immunisation_status === null || item.immunisation_status === undefined) && !isStudentNotEnrolled}
                                                />
                                                {((item.immunisation_status === null || item.immunisation_status === undefined) && !isStudentNotEnrolled) && <FormHelperText className={classes.FormHelperText}>Immunisation Status cannot be empty</FormHelperText>}
                                            </Box>
                                            <Box id="student-field-allergy" sx={{ flex: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                                    <InputLabel className={classes.selectLabel} sx={{ mb: 0 }}>Allergy (Required)</InputLabel>
                                                    <InfoImage
                                                        image={medicareImage7}
                                                        title="Allergy"
                                                    />
                                                </Box>
                                                <IOSSegmentedControl
                                                    options={[{ label: 'YES', value: 'Y' }, { label: 'NO', value: 'N' }]}
                                                    value={item.allergy}
                                                    onChange={(val) => handleInputChange(index, 'allergy', val)}
                                                    disabled={isStudentNotEnrolled}
                                                    error={(item.allergy === null || item.allergy === undefined) && !isStudentNotEnrolled}
                                                />
                                                {((item.allergy === null || item.allergy === undefined) && !isStudentNotEnrolled) && <FormHelperText className={classes.FormHelperText}>Allergy cannot be empty</FormHelperText>}
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                                    <InputLabel className={classes.selectLabel} sx={{ mb: 0 }}>
                                                        Do {getStudentGivenName(item) || 'this student'} have temporary visa? (Required)
                                                    </InputLabel>
                                                    <InfoImage
                                                        image={medicarecard}
                                                        title="Temporary Visa"
                                                    />
                                                </Box>
                                                <IOSSegmentedControl
                                                    options={[{ label: 'YES', value: 'YES' }, { label: 'NO', value: 'NO' }]}
                                                    value={item.istemporaryVisa ? 'YES' : 'NO'}
                                                    onChange={(val) => handleInputChange(index, 'temporary_visa', val === 'YES')}
                                                    disabled={isStudentNotEnrolled}
                                                />
                                            </Box>
                                            
                                            <Box sx={{ display: { xs: 'none', md: 'block' } }} />

                                            {!item.istemporaryVisa && (
                                                <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: '20px', mt: 1 }}>
                                                    <Box sx={{ gridColumn: { md: 'span 2' }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', position: 'relative' }}>
                                                            {!(item.hasCustomMedicare || props.parentNoMedicare) ? (
                                                                <Box id="student-field-family_medicare_number" sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 1 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Box sx={{ flex: 1, bgcolor: '#F1F5F9', p: 1.5, borderRadius: 1.5, border: '1px solid #E2E8F0' }}>
                                                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>Family Medicare Card</Typography>
                                                                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#0F172A' }}>{props.parentMedicareNumber || 'None'}</Typography>
                                                                        </Box>
                                                                        <Box id="student-field-medicare_ref_no" sx={{ width: '120px' }}>
                                                                            <TextField
                                                                                required
                                                                                label="Ref No"
                                                                                variant="outlined"
                                                                                disabled={item.istemporaryVisa || isStudentNotEnrolled}
                                                                                value={item.temporary_visa ? 'NA' : item.medicare_ref_no || ''}
                                                                                onChange={(e) => handleInputChange(index, 'medicare_ref_no', e.target.value.replace(/\D/g, '').substring(0, 1))}
                                                                                inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                                                                                error={!isStudentNotEnrolled && !item.istemporaryVisa && !/^\d$/.test(String(item.medicare_ref_no ?? ''))}
                                                                                helperText={
                                                                                    (!isStudentNotEnrolled && !item.istemporaryVisa && !/^\d$/.test(String(item.medicare_ref_no ?? '')))
                                                                                        ? 'Required'
                                                                                        : ''
                                                                                }
                                                                                sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '8px' }, width: '100%' }}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Box id="student-field-medicare_number" sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 1 }}>
                                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                        <TextField
                                                                            required
                                                                            label="10-Digit Medicare Number"
                                                                            className={`${classes.txtfld} ${isStudentNotEnrolled ? classes.NA : item.validMedicareNumber === null ? classes.NA : item.validMedicareNumber ? classes.ValidMedicareNumber : classes.InValidMedicareNumber}`}
                                                                            placeholder="XXXXXXXXXX"
                                                                            variant="outlined"
                                                                            disabled={item.istemporaryVisa || isStudentNotEnrolled}
                                                                            value={item.temporary_visa ? 'NA' : getMedicareBase(item.medicare_number)}
                                                                            onChange={(e) => handleMedicarePartChange(index, 'main', e.target.value)}
                                                                            inputProps={{ maxLength: 10 }}
                                                                            InputProps={{ startAdornment: (<InputAdornment position="start"><LocalHospitalIcon /></InputAdornment>) }}
                                                                            error={!isStudentNotEnrolled && !item.istemporaryVisa && !item.validMedicareNumber}
                                                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '8px' }, flex: 1 }}
                                                                        />
                                                                        <Typography sx={{ fontWeight: 700 }}>-</Typography>
                                                                        <TextField
                                                                            required
                                                                            label="Ref No"
                                                                            className={`${classes.txtfld} ${isStudentNotEnrolled ? classes.NA : item.validMedicareNumber === null ? classes.NA : item.validMedicareNumber ? classes.ValidMedicareNumber : classes.InValidMedicareNumber}`}
                                                                            placeholder="X"
                                                                            variant="outlined"
                                                                            disabled={item.istemporaryVisa || isStudentNotEnrolled}
                                                                            value={item.temporary_visa ? 'NA' : getMedicareRef(item.medicare_number)}
                                                                            onChange={(e) => handleMedicarePartChange(index, 'suffix', e.target.value)}
                                                                            inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                                                                            error={!isStudentNotEnrolled && !item.istemporaryVisa && !item.validMedicareNumber}
                                                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '8px' }, width: '80px' }}
                                                                        />
                                                                    </Box>
                                                                    {(!item.validMedicareNumber && !item.istemporaryVisa && !isStudentNotEnrolled) && (
                                                                        <Typography variant="caption" sx={{ color: 'red', mt: 0.5 }}>Invalid Medicare Number</Typography>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        {!props.parentNoMedicare && (
                                                            <FormControlLabel
                                                                control={<Checkbox checked={item.hasCustomMedicare || false} onChange={(e) => handleInputChange(index, 'hasCustomMedicare', e.target.checked)} disabled={item.istemporaryVisa || isStudentNotEnrolled} size="small" />}
                                                                label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>Student has a different Medicare card</Typography>}
                                                            />
                                                        )}
                                                    </Box>

                                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    
                                                    {!(item.hasCustomMedicare || props.parentNoMedicare) ? (
                                                        <Box id="student-field-family_medicare_expiry" sx={{ bgcolor: '#F1F5F9', p: 1.5, borderRadius: 1.5, border: '1px solid #E2E8F0', height: 'fit-content' }}>
                                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>Family Medicare Expiry</Typography>
                                                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#0F172A' }}>{props.parentMedicareExpiry || 'None'}</Typography>
                                                        </Box>
                                                    ) : (
                                                        <Box id="student-field-medicare_expiry" classes={classes.MedExpCont}>
                                                            {(() => {
                                                                let errorMessage = '';
                                                                if (!item.istemporaryVisa && !isStudentNotEnrolled) {
                                                                    if (!item.medicare_expiry || item.medicare_expiry === 'NA') {
                                                                        errorMessage = 'Medicare expiry date is required.';
                                                                    } else if (!isValidMonthYear(item.medicare_expiry)) {
                                                                        errorMessage = 'Please enter a valid expiry date in MM/YYYY format.';
                                                                    } else if (!isMonthYearInFuture(item.medicare_expiry)) {
                                                                        errorMessage = 'Expiry date must be later than the current month.';
                                                                    }
                                                                }
                                                                return (
                                                                    <MedicareExpiryDatePicker
                                                                        disabled={item.istemporaryVisa || isStudentNotEnrolled}
                                                                        defaultValue={item.medicare_expiry !== null ? item.medicare_expiry : ''}
                                                                        index={index}
                                                                        key_prop={'medicare_expiry'}
                                                                        setMedicareExpiryDate={handleInputChange}
                                                                        error={errorMessage ? [{ index: 'medicare_expiry' }] : []}
                                                                        errorMessage={errorMessage}
                                                                    />
                                                                );
                                                            })()}
                                                        </Box>
                                                    )}
                                                    </Box>

                                                    {(item.hasCustomMedicare || props.parentNoMedicare) && (() => {
                                                    // One key for storing and for looking up. These
                                                    // used to differ — a number one side, a string
                                                    // the other — so a student without a code lost
                                                    // their upload at save time.
                                                    const cardKey = studentCode || String(index);
                                                    const storedCard = props.studentStoredCards?.[studentCode] || [];
                                                    return (
                                                        <Box id="student-field-medicare_images" sx={{
                                                            gridColumn: '1 / -1',
                                                            p: 2,
                                                            mt: 1,
                                                            borderRadius: '12px',
                                                            bgcolor: '#FFF7ED',
                                                            border: '1px solid #FDBA74',
                                                        }}>
                                                            <Typography sx={{ color: '#9A3412', fontWeight: 700, mb: 0.5 }}>
                                                                Upload {getStudentGivenName(item) || 'student'}’s Medicare card
                                                            </Typography>
                                                            <Typography sx={{ color: '#9A3412', fontSize: '13px', mb: 2 }}>
                                                                Both the front and back images are required for a different Medicare card.
                                                            </Typography>
                                                            <MedicareUpload
                                                                files={props.studentMedicareImages?.[cardKey] || []}
                                                                SetMedicareImages={(files) =>
                                                                    props.setStudentMedicareImages?.(cardKey, files)
                                                                }
                                                                storedUrls={storedCard}
                                                                onRemoveStored={(slot) =>
                                                                    props.onRemoveStudentStoredCard?.(cardKey, slot)
                                                                }
                                                                error={
                                                                    cardSideMissing(
                                                                        props.studentMedicareImages?.[cardKey] || [],
                                                                        storedCard
                                                                    )
                                                                        ? 'Medicare Front and Back Images are required'
                                                                        : ''
                                                                }
                                                            />
                                                        </Box>
                                                    );
                                                    })()}
                                                </Box>
                                            )}
                                            </Box>
                                        </td>
                                    </tr>
                                    {isNotEligible &&
                                        <tr>
                                            <td className={classes.Btnstd}>
                                                <p style={{ fontSize: '14px', fontStyle: 'italic', color: 'red' }}>
                                                    Not Eligible for Re-enrolment
                                                </p>
                                            </td>
                                        </tr>
                                    }
                                    {!isNotEligible && item.enrollStatus === 'Not Enrolled' &&
                                        <tr>
                                            <td className={classes.Btnstd}>
                                                <p style={{ fontSize: '14px', fontStyle: 'italic', color: 'red' }}>
                                                    Please note that by unselecting the students, you will not be able to re-enrol your student at a later date. All payments and fees is calculated based on the selected re-enroled student.
                                                </p>
                                            </td>
                                        </tr>
                                    }
                                </>
                            </tbody>
                        </table>
                    </div>
                </div>
            </React.Fragment>
        );
    });

    if (props.singleStudentIndex !== undefined) {
        return (
            <div className={classes.StudentMedicalForm}>
                {renderItems[props.singleStudentIndex] || renderItems[0]}
            </div>
        );
    }

    if (formData.length > 1) {
        const activeStudent = formData[activeStudentTab] || formData[0];
        const totalStudents = formData.length;
        const isLastStudent = activeStudentTab === totalStudents - 1;
        const isFirstStudent = activeStudentTab === 0;

        return (
            <Box className={classes.StudentMedicalForm} sx={{ width: '100%' }}>
                {/* One Student Focus Header Card */}
                <Box
                    sx={{
                        p: 2,
                        mb: 2.5,
                        borderRadius: '16px',
                        bgcolor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            sx={{
                                bgcolor: '#059669',
                                color: '#FFF',
                                fontWeight: 800,
                                width: 44,
                                height: 44,
                                boxShadow: '0 2px 8px rgba(5, 150, 105, 0.25)'
                            }}
                        >
                            {getStudentGivenName(activeStudent) ? getStudentGivenName(activeStudent)[0] : activeStudentTab + 1}
                        </Avatar>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={`Student ${activeStudentTab + 1} of ${totalStudents}`}
                                    size="small"
                                    sx={{ bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 700, fontSize: '11px' }}
                                />
                                {activeStudent?.enrollStatus === 'Not Enrolled' && (
                                    <Chip label="Not Enrolling" size="small" sx={{ bgcolor: '#FEF2F2', color: '#991B1B', fontWeight: 700, fontSize: '11px' }} />
                                )}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1.1rem', mt: 0.25 }}>
                                {getStudentFullName(activeStudent)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Progress Indicator */}
                    <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                            Child {activeStudentTab + 1} of {totalStudents}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={((activeStudentTab + 1) / totalStudents) * 100}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: '#E2E8F0',
                                '& .MuiLinearProgress-bar': { bgcolor: '#059669', borderRadius: 3 },
                                mt: 0.5
                            }}
                        />
                    </Box>
                </Box>

                {/* Active Student Medical Form Card ONLY */}
                {renderItems[activeStudentTab] || renderItems[0]}

                {/* Stepper Navigation */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 3,
                        pt: 2,
                        borderTop: '1px solid #E2E8F0',
                        gap: 1.5
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<NavigateBeforeIcon />}
                        disabled={isFirstStudent}
                        onClick={() => {
                            setActiveStudentTab(prev => Math.max(0, prev - 1));
                            window.scrollTo({ top: 400, behavior: 'smooth' });
                        }}
                        sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: '#CBD5E1',
                            color: '#475569',
                            '&:hover': { borderColor: '#94A3B8' }
                        }}
                    >
                        Previous Student
                    </Button>

                    {!isLastStudent ? (
                        <Button
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            onClick={() => {
                                setActiveStudentTab(prev => Math.min(totalStudents - 1, prev + 1));
                                window.scrollTo({ top: 400, behavior: 'smooth' });
                            }}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                bgcolor: '#059669',
                                color: '#FFFFFF',
                                px: 2.5,
                                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                                '&:hover': { bgcolor: '#047857' }
                            }}
                        >
                            Next Student ({getStudentGivenName(formData[activeStudentTab + 1]) || ''})
                        </Button>
                    ) : (
                        <Chip
                            icon={<CheckCircleTwoToneIcon style={{ color: '#059669' }} />}
                            label="All Student Medical Details Completed"
                            sx={{ bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 700, py: 1, px: 1, borderRadius: '12px' }}
                        />
                    )}
                </Box>
            </Box>
        );
    }

    return <div className={classes.StudentMedicalForm}>{renderItems}</div>;
};

export default StudentMedicalForm;